import React, { useMemo, useRef } from 'react';
import { Box, Paper, Stack, Typography } from '@esgian/esgianui';
import moment from 'moment';
import { useTheme } from '../../../hooks';
import { CanvasGroupedTimeSeriesBarChart } from '..';
import { useSelector } from 'react-redux';
import { getMainPageSlice } from '../../../store/features/filters/MainPageSlice/MainPageSlice';
import { NoDataAvailable } from '../../Display';

type EventType = 'TooManyHPU' | 'DW_AUX_Idle' | 'MP_AUX_Idle' | 'TooManyEng' | 'TooManyAgitators';

type ChartDataItem = {
  startDate: string;
  endDate: string;
  eventName: string;
  backgroundColor: string;
  saving: string;
  potentialSaving: string;
  subPowerPlant?: (string | number)[] | string | null;
};

type Event = {
  startDate: string;
  endDate: string;
  type: EventType;
  costSaving: string;
  fuelSaving: string;
  subPowerPlant?: (string | number)[] | string | null;
};

type FocusAreaTimelineChartProps = {
  loading?: boolean;
  data?: Event[];
};

const LOOKUP: Record<EventType, string> = {
  TooManyEng: 'Engines',
  MP_AUX_Idle: 'Mud pumps',
  TooManyHPU: 'HPU',
  DW_AUX_Idle: 'Drawworks',
  TooManyAgitators: 'Agitators'
};

const FocusAreaTimelineChart: React.FC<FocusAreaTimelineChartProps> = ({ loading, data }) => {
  const { startDate: selectedStartDate, endDate: selectedEndDate } = useSelector(getMainPageSlice);
  const { theme } = useTheme();
  const {
    palette: {
      primary: { main }
    }
  } = theme;
  const ref = useRef<HTMLCanvasElement>(null);

  const chartData = useMemo<ChartDataItem[]>(() => {
    if (!loading && data?.length) {
      const typeOrder = Object.keys(LOOKUP) as EventType[];
      return [...data]
        .sort((a, b) => {
          const typeIndexDiff = typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
          if (typeIndexDiff !== 0) return typeIndexDiff;
          if (a.subPowerPlant === b.subPowerPlant) return 0;
          if (!a.subPowerPlant) return -1;
          if (!b.subPowerPlant) return 1;
          return String(a.subPowerPlant).localeCompare(String(b.subPowerPlant));
        })
        .map((event) => {
          const baseName = LOOKUP[event.type];
          const eventName = event.subPowerPlant ? `${baseName} (Swbd ${event.subPowerPlant})` : baseName;
          return {
            startDate: moment.parseZone(event.startDate).format('YYYY-MM-DDTHH:mm:ss'),
            endDate: moment.parseZone(event.endDate).format('YYYY-MM-DDTHH:mm:ss'),
            eventName,
            backgroundColor: main,
            saving: `${event?.fuelSaving || '-'} mt`,
            potentialSaving: `${event?.costSaving || '-'} USD`,
            subPowerPlant: event.subPowerPlant
          };
        });
    }
    return [];
  }, [data, loading, main]);

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack>
        <Typography variant="h6">Focus area timeline</Typography>
        <Typography variant="body2">Active focus areas</Typography>
      </Stack>
      {!loading && !chartData?.length ? (
        <NoDataAvailable height="200px" />
      ) : (
      <CanvasGroupedTimeSeriesBarChart
        chartRef={ref}
        loading={loading}
        stackedBar={true}
        data={chartData || []}
        min={selectedStartDate}
        max={selectedEndDate}
        displayedDaysCount={14}
      />
      )}
    </Paper>
  );
};

export default FocusAreaTimelineChart;
